<template>
  <q-dialog
    v-model="shown"
    maximized
    class="scroll"
  >
    <div
      class="fullscreen items-center justify-center scroll"
      :class="{
        'column': $q.screen.gt.xs,
        'row': $q.screen.xs
      }"
      style="box-shadow: none;"
      @click="shown = false"
    >
      <q-card
        v-for="termOption in termOptions"
        :key="termOption.term"
        class="q-ma-md cursor-pointer"
        :class="{
          'col-auto': $q.screen.gt.xs,
          'col': $q.screen.xs,
          selected: termOption.term === membership.term
        }"
        style="max-width: 400px; min-width: 300px;"
        @click.stop="selectTerm(termOption.term)"
      >
        <q-card-section
          class="row items-center q-mx-xs"
        >
          <div class="text-h6">
            {{ termOption.termName }}
          </div>
          <q-space />
          <q-icon
            v-if="invalidTermOptions[termOption.term]"
            name="warning"
            color="warning"
            size="md"
          >
            <q-tooltip class="bg-warning">
              {{ $t('membership.invalidRenewalPeriodTooltip') }}
            </q-tooltip>
          </q-icon>
        </q-card-section>
        <q-card-section
          class="row items-center q-mx-xs"
        >
          <q-item-label
            v-for="detail in termOption.detailDescription"
            :key="detail.key"
          >
            - {{ detail.description }}
          </q-item-label>
          <div v-if="termOption.ticketRewardDescription">
            {{ termOption.ticketRewardDescription }}
          </div>
        </q-card-section>
        <q-card-section
          class="row items-center justify-between q-mx-xs"
        >
          <div class="text-h5">
            <i18n-n
              :value="membershipRateDisplayTotal(termOption)"
              format="currency"
              scope="global"
            />
            / {{ membershipRateDisplayPeriodName() }}
          </div>
          <div
            v-if="termOption.monthlyRate && termOptionsDiscounts[termOption.term]"
            class="text-h6"
          >
            {{ $t('membership.saveAmountHeading', { amount: `${termOptionsDiscounts[termOption.term]}%` }) }}
          </div>
        </q-card-section>
      </q-card>
    </div>
  </q-dialog>
</template>

<script>
import Membership from 'src/models/membership/Membership'

export default {
  name: 'SelectTermDialog',
  configuration: ['currentCircuit', 'membershipTypes'],
  props: {
    membership: {
      type: Membership,
      required: true
    }
  },
  emits: ['select-term'],
  data () {
    return {
      shown: false
    }
  },
  computed: {
    anyMembershipTermOverrides () {
      return _.filter(this.membershipTypes.data, 'overrideCircuitTermOptions').length
    },
    termOptions () {
      const termOptions = []
      let membershipCopy
      const ownerMembershipType = this.membership.owner.membershipType

      let allowMonthlyMembership = this.currentCircuit.allowMonthlyMembership
      let allowQuarterlyMembership = this.currentCircuit.allowQuarterlyMembership
      let allowBiannualMembership = this.currentCircuit.allowBiannualMembership
      let allowAnnualMembership = this.currentCircuit.allowAnnualMembership

      if (this.anyMembershipTermOverrides) {
        _.each(this.membershipTypes.data, membershipType => {
          if (this.$consumer && !membershipType.availableOnline && !membershipType.allowRenewalOnline) {
            return
          }

          if (membershipType.allowMonthlyMembership) {
            allowMonthlyMembership = true
          }
          if (membershipType.allowQuarterlyMembership) {
            allowQuarterlyMembership = true
          }
          if (membershipType.allowBiannualMembership) {
            allowBiannualMembership = true
          }
          if (membershipType.allowAnnualMembership) {
            allowAnnualMembership = true
          }
        })
      }

      if (allowMonthlyMembership && (!ownerMembershipType.hasYearlyTicketVouchers || ownerMembershipType.hasMonthlyTicketVouchers)) {
        membershipCopy = this.membership.copy()
        membershipCopy.term = 1
        termOptions.push(membershipCopy)
      }

      if (allowQuarterlyMembership && (!ownerMembershipType.hasYearlyTicketVouchers || ownerMembershipType.hasMonthlyTicketVouchers)) {
        membershipCopy = this.membership.copy()
        membershipCopy.term = 3
        termOptions.push(membershipCopy)
      }

      if (allowBiannualMembership && (!ownerMembershipType.hasYearlyTicketVouchers || ownerMembershipType.hasMonthlyTicketVouchers)) {
        membershipCopy = this.membership.copy()
        membershipCopy.term = 6
        termOptions.push(membershipCopy)
      }

      if (allowAnnualMembership) {
        membershipCopy = this.membership.copy()
        membershipCopy.term = 12
        termOptions.push(membershipCopy)
      }

      return termOptions
    },
    termOptionsDiscounts () {
      const baseMembership = this.membership.copy()
      baseMembership.term = 1

      const termOptionsDiscounts = {}

      _.each(this.termOptions, termOption => {
        termOptionsDiscounts[termOption.term] = Math.round((1 - ((termOption.monthlyRate) / (baseMembership.monthlyRate))) * 100)
      })

      return termOptionsDiscounts
    },
    invalidTermOptions () {
      const invalidTermOptions = { 1: false, 3: false, 6: false, 12: false }

      _.each(this.membership.members.data, member => {
        if ((member.membershipType.overrideCircuitTermOptions && !member.membershipType.allowMonthlyMembership) ||
            (!member.membershipType.overrideCircuitTermOptions && !this.currentCircuit.allowMonthlyMembership)) {
          invalidTermOptions[1] = true
        }
        if ((member.membershipType.overrideCircuitTermOptions && !member.membershipType.allowQuarterlyMembership) ||
            (!member.membershipType.overrideCircuitTermOptions && !this.currentCircuit.allowQuarterlyMembership)) {
          invalidTermOptions[3] = true
        }
        if ((member.membershipType.overrideCircuitTermOptions && !member.membershipType.allowBiannualMembership) ||
            (!member.membershipType.overrideCircuitTermOptions && !this.currentCircuit.allowBiannualMembership)) {
          invalidTermOptions[6] = true
        }
        if ((member.membershipType.overrideCircuitTermOptions && !member.membershipType.allowAnnualMembership) ||
            (!member.membershipType.overrideCircuitTermOptions && !this.currentCircuit.allowAnnualMembership)) {
          invalidTermOptions[12] = true
        }
      })

      return invalidTermOptions
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    membershipRateDisplayTotal (membership) {
      return membership.monthlyRate * this.currentCircuit.membershipRateDisplayPeriod
    },
    membershipRateDisplayPeriodName () {
      switch (this.currentCircuit.membershipRateDisplayPeriod) {
        case 1:
          return _.toLower(this.$tc('time.month', 1))
        case 12:
          return _.toLower(this.$tc('time.year', 1))
        default:
          // Something has gone wrong
          return _.toLower(this.$tc('time.month', 1))
      }
    },
    selectTerm (term) {
      this.membership.term = term

      setTimeout(() => { this.shown = false }, 150)

      this.$emit('select-term')
    }
  }
}

</script>

<style lang="scss" scoped>
  .title {
    font-family: "Roboto", "-apple-system", "Helvetica Neue", Helvetica, Arial, sans-serif;
  }
  .selected {
    border: solid 4px;
    border-radius: 10px;
    border-color: var(--q-primary);
  }
</style>
